/**
 * TEMPORARY SCRIP-40 bridge for retained pre-migration Solr documents.
 * Delete this module and its query hook AFTER production finishes BOTH indexes.
 * Reads API v2 only; never writes to Drupal or Solr. Keep translation revisions
 * unchanged between migration and completion of reindexing: legacy documents
 * contain no revision, so their revision must be resolved via v2.
 */
import type { ScriptureClientInterface } from "@rsc/scripture-client";
import { canonicalizeTranslationId } from "~/units/scripture/navigation";
import type { CommentarySearchResultsGroup } from "./types";

type RecordData = Record<string, unknown>;
function record(value: unknown): value is RecordData {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export async function normalizeTemporarySolrGroups(
  groups: CommentarySearchResultsGroup[],
  client?: ScriptureClientInterface,
): Promise<CommentarySearchResultsGroup[]> {
  // Request-scoped caches avoid duplicate lookups and cross-request/SSR state.
  const revisions = new Map<string, Promise<string>>();
  const verses = new Map<string, Promise<unknown>>();
  async function revision(tid: string): Promise<string> {
    if (!client)
      throw new Error("Legacy Solr data requires the Scripture v2 client.");
    if (!revisions.has(tid)) {
      revisions.set(
        tid,
        client.getTranslation(tid).match(
          (translation) => translation.tr,
          (error) => {
            throw error;
          },
        ),
      );
    }
    return revisions.get(tid)!;
  }
  async function normalize(value: unknown): Promise<unknown> {
    if (!record(value)) return value;
    const source = value.verse ?? value.range;
    if (
      !record(source) ||
      typeof source.tid !== "string" ||
      !source.tid ||
      source.tr != null
    )
      return value;
    // Leave malformed documents to the existing strict validator.
    if (typeof source.fromVid !== "number" || typeof source.toVid !== "number")
      return value;
    const tid = canonicalizeTranslationId(source.tid);
    const tr = await revision(tid);
    if (!value.verse) return { ...value, range: { ...source, tid, tr } };
    const key = JSON.stringify([tid, tr, source.fromVid]);
    if (!verses.has(key)) {
      verses.set(
        key,
        client!.getVerseById(tid, source.fromVid, undefined, { tr }).match(
          (verse) => verse,
          (error) => {
            throw error;
          },
        ),
      );
    }
    const verse = await verses.get(key);
    if (
      !record(verse) ||
      verse.fromVid !== source.fromVid ||
      verse.toVid !== source.toVid
    ) {
      throw new Error(
        "Legacy Solr verse boundaries differ from the resolved v2 revision.",
      );
    }
    return { ...value, verse };
  }
  return Promise.all(
    groups.map(async (group) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(group.groupValue);
      } catch {
        return group;
      }
      // Older indexes also used a single object rather than an array.
      const values = Array.isArray(parsed)
        ? parsed
        : record(parsed)
          ? [parsed]
          : null;
      if (!values) return group;
      return {
        ...group,
        groupValue: JSON.stringify(await Promise.all(values.map(normalize))),
      };
    }),
  );
}
